import { useState, useRef, useCallback, useLayoutEffect, useEffect, useId } from "react";

// Custom SVG area chart — replaces recharts (banned per /taste anti-patterns)
//
// SIZING NOTE: viewBox is set to actual measured pixel width via ResizeObserver,
// NOT a hardcoded 800. preserveAspectRatio is left at default (xMidYMid meet) so
// 1 SVG unit == 1 screen pixel. This means CSS font-size on `<text>` lands at the
// requested px on screen instead of being deformed by a non-uniform stretch
// (which is what `preserveAspectRatio="none"` + fixed 800 viewBox used to do —
// glyphs got squished or widened depending on container width).
export interface AreaChartSeries {
  key: string;
  color: string;
  fillOpacity?: number;
  dashed?: boolean;
  noFill?: boolean;
}

export interface SVGAreaChartProps {
  data: Array<Record<string, unknown>>;
  series: AreaChartSeries[];
  xKey: string;
  height: number;
  stacked?: boolean;
  /** Series keys the Y axis scales to. Omit (the default) and the axis scales to
   *  EVERY series, which is the pre-S89 behaviour every other caller still gets.
   *  Supply it when one series is so much larger than the rest that it flattens
   *  them into the floor — DM Health's ManyChat-inflated "Conversations Created"
   *  is the canonical case. Series outside the set still RENDER; they are just
   *  not allowed to set the scale, and they clip at the top of the plot. */
  scaleToKeys?: string[];
}

export function SVGAreaChart({
  data,
  series,
  xKey,
  height,
  stacked = false,
  scaleToKeys,
}: SVGAreaChartProps) {
  // Unique per instance so two charts on one page cannot share a clipPath id.
  const clipId = `chart-plot-clip-${useId().replace(/:/g, "")}`;
  const [tooltip, setTooltip] = useState<{ x: number; y: number; idx: number } | null>(null);

  // Measure actual container width — same callback-ref + useLayoutEffect pattern
  // as FinancialGraph.tsx (avoids the blank-on-cold-mount bug where useRef +
  // useEffect([]) reads 0×0 before parent flex has computed its size).
  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [width, setWidth] = useState(0);

  const setContainerNode = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    containerRef.current = node;
    if (!node) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (w > 0 && w !== width) setWidth(w);
  });

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const pad = { top: 20, right: 16, bottom: 44, left: 48 };
  const vw = width;
  const vh = height;
  const cw = Math.max(0, vw - pad.left - pad.right);
  const ch = vh - pad.top - pad.bottom;

  // Compute max Y. `scaleToKeys` narrows WHICH series get a vote on the scale;
  // it never removes a series from the render. Falling back to the full list
  // when the filter matches nothing keeps a typo'd key from collapsing the axis
  // to 1 and clipping every line in the chart.
  const scaleSeries = (() => {
    if (!scaleToKeys || scaleToKeys.length === 0) return series;
    const picked = series.filter((s) => scaleToKeys.includes(s.key));
    return picked.length > 0 ? picked : series;
  })();

  let peakY = 0;
  if (data.length) {
    if (stacked) {
      data.forEach((d) => {
        let sum = 0;
        scaleSeries.forEach((s) => {
          sum += Number(d[s.key]) || 0;
        });
        if (sum > peakY) peakY = sum;
      });
    } else {
      data.forEach((d) => {
        scaleSeries.forEach((s) => {
          const v = Number(d[s.key]) || 0;
          if (v > peakY) peakY = v;
        });
      });
    }
  }
  peakY = peakY || 1;

  // Nice-number axis. Pick a clean TICK STEP first, then set maxY = step × 4, so
  // all five ticks land on round values AND the tallest scaled series still fills
  // most of the plot.
  //
  // The old code rounded maxY itself to one order of magnitude
  // (`ceil(maxY / 10^floor(log10 maxY))`), which padded a peak of ~1,700 up to a
  // hard 2,000 and pushed the real data into the bottom of the box. That is the
  // "static 2k" Mako called out. Stepping instead of rounding keeps the peak
  // between roughly 75% and 100% of the axis at every magnitude.
  const TICK_INTERVALS = 4;
  const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  const rawStep = peakY / TICK_INTERVALS;
  const stepMag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const stepN = rawStep / stepMag;
  let step = (NICE_STEPS.find((n) => stepN <= n) ?? 10) * stepMag;
  // Every series this chart draws is an integer count, so never emit a
  // fractional tick (a 1.5 step at magnitude 1 would print "1.5, 3, 4.5").
  step = stepMag < 10 ? Math.max(1, Math.ceil(step)) : step;
  const maxY = step * TICK_INTERVALS;

  const xStep = data.length > 1 ? cw / (data.length - 1) : cw;
  const toY = (v: number) => pad.top + ch - (v / maxY) * ch;
  const toX = (i: number) => pad.left + i * xStep;

  // Build paths
  const paths: { path: string; fillPath: string; s: AreaChartSeries }[] = [];
  const bases = data.map(() => 0);

  series.forEach((s) => {
    const points: [number, number][] = [];
    const basePoints: [number, number][] = [];

    data.forEach((d, i) => {
      const val = Number(d[s.key]) || 0;
      const base = stacked ? bases[i] : 0;
      points.push([toX(i), toY(base + val)]);
      basePoints.push([toX(i), toY(base)]);
      if (stacked) bases[i] += val;
    });

    const lineParts = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`);
    const path = lineParts.join(" ");

    const fillParts = [
      ...lineParts,
      ...basePoints.reverse().map((p, i) => `${i === 0 ? "L" : "L"}${p[0]},${p[1]}`),
      "Z",
    ];
    const fillPath = fillParts.join(" ");
    paths.push({ path, fillPath, s });
  });

  const yTicks = Array.from({ length: TICK_INTERVALS + 1 }, (_, i) => step * i);
  const labelEvery = Math.max(1, Math.floor(data.length / 8));

  // Y-axis tick text. `(v / 1000).toFixed(0)` printed BOTH 1500 and 2000 as "2k",
  // so the axis showed the same label twice — Mako's "2k and 2k". Keep one decimal
  // for a non-whole thousand so 1500 reads "1.5k" and stays distinct from "2k".
  const formatTick = (v: number) => {
    if (v < 1000) return String(Number.isInteger(v) ? v : Math.round(v * 10) / 10);
    const k = v / 1000;
    return `${Number.isInteger(k) ? k : k.toFixed(1)}k`;
  };

  // Container is always rendered so the ResizeObserver has a node to measure.
  // SVG content gates on data + measured width.
  const ready = data.length > 0 && width > 0;

  return (
    <div ref={setContainerNode} className="relative w-full" style={{ height }}>
      {ready && (
        <svg
          width={vw}
          height={vh}
          viewBox={`0 0 ${vw} ${vh}`}
          className="block"
          onMouseLeave={() => setTooltip(null)}
        >
          {/* Grid lines */}
          {yTicks.map((v, i) => (
            <line
              key={i}
              x1={pad.left}
              x2={vw - pad.right}
              y1={toY(v)}
              y2={toY(v)}
              stroke="var(--glass-border)"
              strokeDasharray="3 3"
              strokeWidth={0.5}
            />
          ))}

          {/* Plot clip. With `scaleToKeys` a series is allowed to exceed maxY, and
              SVG does not clip by default — an off-scale path would paint straight
              over the card header and the legend. Clipping from y=0 keeps the small
              overshoot region above the top gridline usable while still cutting the
              path off before it reaches the x-axis labels. */}
          <defs>
            <clipPath id={clipId}>
              <rect x={pad.left} y={0} width={cw} height={pad.top + ch} />
            </clipPath>
          </defs>

          <g clipPath={`url(#${clipId})`}>
            {/* Filled areas */}
            {paths.map(({ fillPath, s }, i) =>
              s.noFill ? null : (
                <path
                  key={`fill-${i}`}
                  d={fillPath}
                  fill={s.color}
                  fillOpacity={s.fillOpacity ?? 0.3}
                />
              )
            )}

            {/* Stroke lines */}
            {paths.map(({ path, s }, i) => (
              <path
                key={`line-${i}`}
                d={path}
                fill="none"
                stroke={s.color}
                strokeWidth={s.dashed ? 1.5 : 2}
                strokeDasharray={s.dashed ? "6 4" : undefined}
              />
            ))}
          </g>

          {/* Hover zones */}
          {data.map((_, i) => (
            <rect
              key={`hover-${i}`}
              x={toX(i) - xStep / 2}
              y={pad.top}
              width={xStep}
              height={ch}
              fill="transparent"
              onMouseEnter={(e) => {
                const rect = (e.target as SVGRectElement).closest("svg")!.getBoundingClientRect();
                const relX = e.clientX - rect.left;
                const relY = e.clientY - rect.top;
                setTooltip({ x: relX, y: relY, idx: i });
              }}
              onMouseLeave={() => setTooltip(null)}
            />
          ))}

          {/* Y-axis labels — DM Mono (numbers always go mono per design system) */}
          {yTicks.map((v, i) => (
            <text
              key={`yt-${i}`}
              x={pad.left - 8}
              y={toY(v) + 3}
              textAnchor="end"
              className="token-13m"
              style={{ fontVariantNumeric: "tabular-nums" }}
              fill="var(--glass-secondary)"
            >
              {formatTick(v)}
            </text>
          ))}

          {/* X-axis labels — Inter caption (date labels, mixed case) */}
          {data.map((d, i) =>
            i % labelEvery === 0 || i === data.length - 1 ? (
              <text
                key={`xt-${i}`}
                x={toX(i)}
                y={vh - pad.bottom + 20}
                textAnchor="middle"
                className="token-10l"
                fill="var(--glass-secondary)"
              >
                {String(d[xKey])}
              </text>
            ) : null
          )}
        </svg>
      )}

      {/* Tooltip — clamp inside chart container so .liquid-glass parent doesn't clip it.
          maxWidth pairs with the clamp budget; flipLeft kicks in near the right edge. */}
      {tooltip &&
        data[tooltip.idx] &&
        (() => {
          const TOOLTIP_W = 220;
          const EDGE_PAD = 8;
          const flipLeft = tooltip.x > vw - TOOLTIP_W - EDGE_PAD;
          const left = flipLeft
            ? Math.max(EDGE_PAD, tooltip.x - TOOLTIP_W - 12)
            : // Floor at EDGE_PAD so a container narrower than TOOLTIP_W never pushes
              // the tooltip off the left edge (the wider 220px surface shrank this margin).
              Math.max(EDGE_PAD, Math.min(tooltip.x + 12, vw - TOOLTIP_W - EDGE_PAD));
          const top = Math.max(EDGE_PAD, Math.min(tooltip.y - 8, vh - 110));
          return (
            <div
              className="z-10 pointer-events-none tt-surface px-3 py-2 text-xs"
              style={{ position: "absolute", left, top, width: TOOLTIP_W }}
            >
              <div className="token-13m text-glass mb-1.5">{String(data[tooltip.idx][xKey])}</div>
              <div className="space-y-0.5">
                {series.map((s) => (
                  <div key={s.key} className="flex items-center gap-2 whitespace-nowrap">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="token-11 text-glass-secondary">{s.key}</span>
                    <span
                      className="token-13m text-glass ml-auto pl-4"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {Number(data[tooltip.idx][s.key] || 0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-1">
        {series.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
            <span className="token-10 text-glass-secondary">{s.key}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SVGAreaChart;
